<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <!-- Container Modal -->
    <div class="w-full max-w-3xl m-3 overflow-hidden transition-all duration-200 bg-gray-800 rounded-lg shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-600">
        <div class="flex items-center gap-3">
          <!-- Merchant Images (Normal and Angry Faces) -->
          <div class="flex gap-2">
            <img
              v-if="merchantData.normalFace"
              :src="merchantData.normalFace"
              alt="Normal Face"
              class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400"
              @error="handleImageError"
            />
            <img
              v-if="merchantData.angryFace"
              :src="merchantData.angryFace"
              alt="Angry Face"
              class="object-cover w-12 h-12 rounded-md border-2 border-indigo-400"
              @error="handleImageError"
            />
          </div>
          <!-- Name & Rarity Stars -->
          <div>
            <h1 class="text-lg font-semibold text-indigo-200">
              {{ merchantData.name }}
            </h1>
            <div v-if="merchantData.rarity" class="flex items-center gap-1 mt-1">
              <template v-for="i in merchantData.rarity" :key="i">
                <i class="text-xs fas fa-star text-yellow-400 sm:text-sm"></i>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- Tabs -->
      <div class="p-4">
        <div class="flex border-b border-gray-600">
          <button
            @click="activeTab = 'info'"
            class="px-3 py-2 text-xs font-medium transition-colors duration-200 sm:text-sm"
            :class="activeTab === 'info' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'info'"
            aria-label="ข้อมูล"
          >
            ข้อมูล
          </button>
          <button
            @click="activeTab = 'items'"
            class="px-3 py-2 text-xs font-medium transition-colors duration-200 sm:text-sm"
            :class="activeTab === 'items' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'items'"
            aria-label="รายการสินค้า"
          >
            รายการสินค้า
          </button>
          <button
            @click="activeTab = 'locations'"
            class="px-3 py-2 text-xs font-medium transition-colors duration-200 sm:text-sm"
            :class="activeTab === 'locations' ? 'text-indigo-200 border-b-2 border-indigo-500' : 'text-indigo-200 hover:bg-gray-700 hover:text-indigo-300'"
            :aria-selected="activeTab === 'locations'"
            aria-label="สถานที่พบเจอ"
          >
            สถานที่พบเจอ
          </button>
        </div>
      </div>
      <!-- Content -->
      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-96">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center py-4">
          <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
        </div>
        <!-- Tab: Info -->
        <div v-else-if="activeTab === 'info'">
          <!-- Basic Info Section -->
          <section>
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ข้อมูลพื้นฐาน</h2>
            <div class="grid grid-cols-2 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">อนุญาตให้ต่อสู้เพื่อปล้น: <strong>{{ merchantData.allowRob ? 'ใช่' : 'ไม่' }}</strong></span>
              </div>
            </div>
          </section>
          <!-- Rob Rewards Section -->
          <section v-if="merchantData.allowRob">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">รางวัลจากการปล้น</h2>
            <div class="grid grid-cols-3 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2">
                <i class="fas fa-coins text-gray-200 text-sm"></i>
                <span class="text-indigo-200"><strong>{{ merchantData.robRewards?.silver || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fas fa-g text-yellow-400 text-sm"></i>
                <span class="text-indigo-200"><strong>{{ merchantData.robRewards?.gold || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fas fa-gem text-blue-400 text-sm"></i>
                <span class="text-indigo-200"><strong>{{ merchantData.robRewards?.diamond || 0 }}</strong></span>
              </div>
            </div>
          </section>
          <!-- Stats Section -->
          <section v-if="merchantData.stats && merchantData.allowRob">
            <h2 class="text-sm font-semibold text-indigo-200 mb-2">ค่าสถานะ</h2>
            <div class="grid grid-cols-3 gap-3 text-xs sm:text-sm">
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Sta: <strong>{{ merchantData.stats.stamina || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Str: <strong>{{ merchantData.stats.str || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Int: <strong>{{ merchantData.stats.int || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Agi: <strong>{{ merchantData.stats.agi || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Def: <strong>{{ merchantData.stats.defense || 0 }}</strong></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="text-indigo-200">Luck: <strong>{{ merchantData.stats.luck || 0 }}</strong></span>
              </div>
            </div>
          </section>
        </div>
        <!-- Tab: Items -->
        <div v-else-if="activeTab === 'items'">
          <section>
            <div v-if="sortedItems.length === 0" class="text-xs text-center text-indigo-200 sm:text-sm">
              ไม่มีสินค้า
            </div>
            <div v-else class="space-y-3 overflow-auto h-40" id="style-1">
              <div v-for="(item, index) in sortedItems" :key="index" class="flex items-center gap-3 p-3 rounded-md bg-gray-700">
                <img
                  v-if="itemDetails[item.name]?.img"
                  :src="itemDetails[item.name].img"
                  alt="Item Image"
                  class="object-cover w-10 h-10 rounded-md border border-indigo-400"
                  @error="handleImageError"
                />
                <div v-else-if="isLoadingItem[item.name]" class="w-10 h-10 flex items-center justify-center">
                  <div class="w-4 h-4 border-t-2 rounded-full animate-spin border-indigo-600"></div>
                </div>
                <div class="flex-1">
                  <div class="flex items-center space-x-1">
                    <h3 class="text-xs font-semibold text-indigo-200 sm:text-sm">{{ item.name }}</h3>
                  <div v-if="item.transferable"><i class="fa-solid fa-arrow-right-arrow-left text-xs text-green-500"></i></div>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-indigo-200 sm:text-sm">
                    <span>{{ item.price }}</span>
                    <i :class="getCurrencyIcon(item.currency)" class="text-xs"></i>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <!-- Tab: Locations -->
        <div v-else-if="activeTab === 'locations'">
          <section>
            <div v-if="loadingLocations" class="flex items-center justify-center py-4">
              <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
            </div>
            <div v-else-if="locations.length === 0" class="text-xs text-center text-indigo-200 sm:text-sm">
              ไม่มีข้อมูลสถานที่พบเจอสำหรับพ่อค้าเร่นี้
            </div>
            <div v-else class="space-y-3 overflow-y-auto" id="style-1">
              <div v-for="map in locations" :key="map.id" class="p-3 rounded-md bg-gray-700">
                <div class="flex items-center gap-3">
                  <img :src="map.imageUrl || 'default-map.png'" alt="Map Image" class="object-cover w-10 h-10 rounded-md" @error="handleImageError" />
                  <div>
                    <h3 class="text-xs font-semibold text-indigo-200 sm:text-sm">{{ map.name }}</h3>
                    <p class="text-xs text-indigo-200 sm:text-sm">เลเวลที่ต้องการ: <strong>{{ map.requiredLevel }}</strong></p>
                  </div>
                </div>
                <div class="mt-2 ml-12 space-y-1 text-xs text-indigo-200 sm:text-sm">
                  <p>โอกาสพบเจอ: <strong>{{ (map.chance / 100).toFixed(2) }}%</strong></p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <!-- Footer -->
      <div class="flex justify-end p-4 border-t border-gray-600 gap-3">
        <button
          v-if="isAdmin"
          @click="$emit('editMerchant', merchantId)"
          class="px-4 py-1 text-xs font-medium text-indigo-200 transition-colors duration-200 bg-indigo-600 rounded-md hover:bg-indigo-700 sm:text-sm"
          aria-label="แก้ไข"
        >
          แก้ไข
        </button>
        <button
          @click="$emit('cancelModal')"
          class="px-4 py-1 text-xs font-medium text-indigo-200 transition-colors duration-200 bg-indigo-600 rounded-md hover:bg-indigo-700 sm:text-sm"
          aria-label="ปิด"
        >
          ปิด
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, computed } from 'vue';
import { projectFirestore } from '@/firebase/config';
import { usePlayerStore } from '@/composables/usePlayerStore';
import gameMerchantsData from '@/data/gameMerchants.json';
import gameItemsData from '@/data/gameItems.json';
export default {
  props: ['merchantId'],
  emits: ['cancelModal', 'editMerchant'],
  setup(props, { emit }) {
    const playerStore = usePlayerStore();
    const merchantData = ref({});
    const isLoading = ref(true);
    const activeTab = ref('info');
    const itemDetails = ref({});
    const isLoadingItem = ref({});
    const locations = ref([]);
    const loadingLocations = ref(false);
    const isAdmin = ref(false);
    const tabs = computed(() => [
      { id: 'info', name: 'ข้อมูล', tooltip: 'ดูข้อมูลพื้นฐานของพ่อค้าเร่' },
      { id: 'items', name: 'รายการสินค้า', tooltip: 'ดูสินค้าที่ขาย' }
    ]);
    const sortedItems = computed(() => {
      if (!merchantData.value.items) return [];
      const order = { diamond: 0, gold: 1, silver: 2 };
      return [...merchantData.value.items].sort((a, b) => {
        if (order[a.currency] !== order[b.currency]) return order[a.currency] - order[b.currency];
        return b.price - a.price;
      });
    });
    const getCurrencyIcon = (currency) => {
      if (currency === 'silver') return 'fas fa-coins text-gray-100';
      if (currency === 'gold') return 'fas fa-g text-yellow-500';
      if (currency === 'diamond') return 'fas fa-gem text-blue-500';
      return '';
    };
    // Fetch merchant data
    const fetchMerchant = async () => {
      try {
        const data = gameMerchantsData[props.merchantId];
        if (data) {
          merchantData.value = data;
          // Fetch item details
          (merchantData.value.items || []).forEach(item => fetchItemDetails(item.name));
        } else {
          alert('ไม่พบข้อมูลพ่อค้าเร่');
          emit('cancelModal');
        }
      } catch (error) {
        console.error('Error fetching merchant:', error);
        alert(`เกิดข้อผิดพลาด: ${error.message}`);
      } finally {
        isLoading.value = false;
      }
    };
    const fetchItemDetails = (itemName) => {
      if (!itemDetails.value[itemName] && !isLoadingItem.value[itemName]) {
        try {
          isLoadingItem.value[itemName] = true;
          itemDetails.value[itemName] = gameItemsData[itemName] || null;
        } catch (error) {
          itemDetails.value[itemName] = null;
        } finally {
          isLoadingItem.value[itemName] = false;
        }
      }
    };
    const fetchLocations = async () => {
      loadingLocations.value = true;
      try {
        const snapshot = await projectFirestore.collection("maps").get();
        const locs = [];
        snapshot.forEach(doc => {
          const mapData = doc.data();
          const merchant = (mapData.merchants || []).find(m => m.name === merchantData.value.name);
          if (merchant) {
            locs.push({
              id: doc.id,
              name: mapData.name,
              imageUrl: mapData.imageUrl || "default-map.png",
              requiredLevel: mapData.requiredLevel || 1,
              chance: merchant.chance || 0
            });
          }
        });
        locations.value = locs.sort((a, b) => b.chance - a.chance); // Sort by chance descending
      } catch (error) {
        console.error("Error fetching locations:", error);
        locations.value = [];
      } finally {
        loadingLocations.value = false;
      }
    };
    const fetchUserRole = () => {
      isAdmin.value = playerStore.userData?.admin === true;
    };
    const handleImageError = (event) => {
      event.target.src = 'https://via.placeholder.com/64?text=Image+Not+Found';
    };
    onMounted(() => {
      fetchUserRole();
      fetchMerchant();
      fetchLocations();
    });
    return {
      merchantData,
      isLoading,
      activeTab,
      tabs,
      sortedItems,
      itemDetails,
      isLoadingItem,
      locations,
      loadingLocations,
      getCurrencyIcon,
      handleImageError,
      isAdmin
    };
  }
};
</script>
<style scoped>
/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}
::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}
::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}
/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
button:not(:disabled):active {
  transform: scale(0.98);
}
</style>